import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  InputAdornment,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { MdSearch, MdPersonSearch, MdPersonOff, MdSwapHoriz, MdFileDownload, MdRefresh } from "react-icons/md";
import { useDispatch, useSelector } from "react-redux";
import axios from "axios";
import xlsx from "json-as-xlsx";
import { API_URL } from "../../utils/constants";
import { cssProperties } from "../../utils/commonCssProperties";
import masterActions from "../../redux/master/actions";
import commonActions from "../../redux/common/actions";
import TablePagination from "../../common/tablePagination";

/**
 * Venue Change Request - a student asks offline to change their allocated venue.
 *
 * Search the candidate by course + Application Number, pick the new exam date,
 * batch and venue, and enter the reason. The candidate takes a free normal seat of
 * the new venue/batch, or a buffer seat (the "Buffer Seats" column of the venue
 * data) only when no normal seat is left; a venue with neither left for the batch
 * cannot be chosen. The roll number never changes. The
 * admit card is not regenerated here - the screen says when it must be regenerated
 * with Single Admit Card generation. See controllers/backoffice/venueChangeController.js.
 */

const API = `${API_URL}/backoffice/venue-change`;

const BRAND = cssProperties?.color?.primary;
const BUTTON_BG = cssProperties?.buttonbgcolor?.primary;
const BUTTON_FG = cssProperties?.buttonfontcolor?.black;
const MUTED = cssProperties?.color?.textsecondary;
const BORDER = cssProperties?.bordercolor?.secondary;

const panelSx = {
  p: { xs: 2, md: 3 },
  mb: 2.5,
  borderRadius: "16px",
  border: `1px solid ${BORDER}`,
  boxShadow: "0 1px 3px rgba(16, 24, 40, 0.06)",
  backgroundColor: cssProperties?.backgroundcolor?.secondary,
};

const labelSx = { mb: 0.75, fontSize: 13, fontWeight: 500, color: MUTED };
const fieldSx = { borderRadius: "10px", backgroundColor: "#fff" };

const pillButtonSx = {
  borderRadius: "40px",
  px: 3,
  py: 1.1,
  fontSize: 14,
  fontWeight: 600,
  letterSpacing: "0.32px",
  boxShadow: "none",
  whiteSpace: "nowrap",
};

const primaryButtonSx = {
  ...pillButtonSx,
  backgroundColor: BUTTON_BG,
  color: BUTTON_FG,
  "&:hover": { backgroundColor: BUTTON_BG, boxShadow: "none", filter: "brightness(0.96)" },
};

const outlineButtonSx = {
  ...pillButtonSx,
  border: `1px solid ${BUTTON_BG}`,
  color: BUTTON_FG,
  "&:hover": { border: `1px solid ${BUTTON_BG}`, backgroundColor: "rgba(230, 151, 171, 0.08)" },
};

const allocationLabel = (a) => {
  const parts = [];
  if (a.ispcm) parts.push("PCM");
  if (a.ispcb) parts.push("PCB");
  if (a.noofattempts) parts.push(`Attempt ${a.noofattempts}`);
  return parts.join(" · ") || "Allocation";
};

const batchLabel = (b) =>
  `Batch ${b.batchno}${b.batchname ? ` - ${b.batchname}` : ""}${b.starttime ? ` (${b.starttime} - ${b.endtime})` : ""}`;

const EmptyState = ({ icon: Icon, title, text }) => (
  <Box sx={{ textAlign: "center", py: { xs: 5, md: 7 }, px: 2 }}>
    <Box
      sx={{
        width: 64,
        height: 64,
        mx: "auto",
        mb: 2,
        borderRadius: "50%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        backgroundColor: cssProperties?.hoverbgcolor?.sidebar,
        color: BRAND,
      }}
    >
      <Icon size={30} />
    </Box>
    <Typography sx={{ fontSize: 16, fontWeight: 600, color: cssProperties?.fontcolor?.primary }}>
      {title}
    </Typography>
    {text && (
      <Typography sx={{ mt: 0.5, fontSize: 14, color: MUTED, maxWidth: 440, mx: "auto" }}>
        {text}
      </Typography>
    )}
  </Box>
);

const Detail = ({ label, value }) => (
  <Box sx={{ minWidth: 0 }}>
    <Typography sx={{ fontSize: 12, color: MUTED, mb: 0.25 }}>{label}</Typography>
    <Typography sx={{ fontSize: 15, fontWeight: 600, wordBreak: "break-word" }}>
      {value || "-"}
    </Typography>
  </Box>
);

// History table rows per page. Paging and search run on the server
// (GET /history?page=&pageSize=&search=), so a course with lakhs of venue
// changes never loads them all into the browser.
const HISTORY_PAGE_SIZE = 10;

const HISTORY_COLUMNS = [
  { label: "Changed On", value: "changeddate" },
  { label: "Application Number", value: "applicationno" },
  { label: "Roll No", value: "rollno" },
  { label: "Subject Group", value: "subjectgroup" },
  { label: "Old Venue", value: "oldvenuename" },
  { label: "Old District", value: "olddistrict" },
  { label: "Old Batch", value: "oldbatchno" },
  { label: "Old Exam Date", value: "oldexamdate" },
  { label: "New Venue", value: "newvenuename" },
  { label: "New District", value: "newdistrict" },
  { label: "New Batch", value: "newbatchno" },
  { label: "New Exam Date", value: "newexamdate" },
  { label: "Seat Type", value: "seattype" },
  { label: "Reason", value: "reason" },
  { label: "Reference No", value: "referenceno" },
  { label: "Admit Card Generated Before Change", value: (row) => (row.isadmitcardgenerated ? "Yes" : "No") },
  {
    label: "Admit Card Status",
    value: (row) => {
      const s = admitCardStatus(row);
      return row.admitcardgeneratedon ? `${s.label} (${row.admitcardgeneratedon})` : s.label;
    },
  },
  { label: "Changed By", value: "changedby" },
];

// Admit Card column. The backend works out admitcardstatus on every load from the
// card's latest generation time against the change time (venueChangeController
// getChangeHistory). Falls back to the old flag if a backend without it answers.
const ADMIT_CARD_STATUS = {
  regenerate: { label: "Regenerate", color: "warning" },
  regenerated: { label: "Regenerated", color: "success" },
  generated: { label: "Generated", color: "success" },
  not_generated: { label: "Not generated" },
};
const admitCardStatus = (row) =>
  ADMIT_CARD_STATUS[row.admitcardstatus] ||
  ADMIT_CARD_STATUS[row.isadmitcardgenerated ? "regenerate" : "not_generated"];

export default function VenueChangeRequest() {
  const dispatch = useDispatch();
  const { courseList } = useSelector((state) => state.masterReducer);

  const [courseId, setCourseId] = useState(0);
  const [applicationNo, setApplicationNo] = useState("");
  const [result, setResult] = useState(null); // { allocations, duplicateGroupOthers }
  const [notFound, setNotFound] = useState(null);
  const [loading, setLoading] = useState(false);

  const [selectedId, setSelectedId] = useState(null);
  const [options, setOptions] = useState(null); // { batches, venues }
  const [optionsLoading, setOptionsLoading] = useState(false);
  const [examDate, setExamDate] = useState("");
  const [batchId, setBatchId] = useState("");
  const [venueId, setVenueId] = useState("");
  const [reason, setReason] = useState("");
  const [referenceNo, setReferenceNo] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [doneMessage, setDoneMessage] = useState(null); // { text, admitCardGenerated }

  // One page of history from the server, plus its totals.
  const [history, setHistory] = useState([]);           // rows on the current page
  const [historyTotal, setHistoryTotal] = useState(0);  // rows matching the search
  const [historyTotalAll, setHistoryTotalAll] = useState(0); // whole course
  const [historyPage, setHistoryPage] = useState(1);
  const [historySearchInput, setHistorySearchInput] = useState(""); // what is typed
  const [historyQuery, setHistoryQuery] = useState("");             // what was searched
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyDownloading, setHistoryDownloading] = useState(false);
  const historyPageCount = Math.max(1, Math.ceil(historyTotal / HISTORY_PAGE_SIZE));
  const historyRequest = useRef(0); // ignores answers to requests that were superseded

  useEffect(() => {
    dispatch({ type: masterActions.GET_COURSE_LIST });
  }, [dispatch]);

  // The shared toast never closes itself - the screen that opens it hides it.
  const alertTimer = useRef(null);
  const hideAlert = () => {
    clearTimeout(alertTimer.current);
    dispatch({ type: commonActions.SET_ALERT, payload: { status: null, show: false, message: null } });
  };
  const showAlert = (status, message) => {
    clearTimeout(alertTimer.current);
    dispatch({ type: commonActions.SET_ALERT, payload: { show: true, status, message } });
    alertTimer.current = setTimeout(hideAlert, 2500);
  };
  useEffect(
    () => () => {
      clearTimeout(alertTimer.current);
      dispatch({ type: commonActions.SET_ALERT, payload: { status: null, show: false, message: null } });
    },
    [dispatch]
  );

  const allocation = result?.allocations?.find((a) => a.venueallocationid === selectedId) || null;

  const loadHistory = async (id, page = 1, query = historyQuery) => {
    const requestNo = ++historyRequest.current;
    if (!id) {
      setHistory([]);
      setHistoryTotal(0);
      setHistoryTotalAll(0);
      return;
    }
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams({ courseid: id, page, pageSize: HISTORY_PAGE_SIZE });
      if (query) params.set("search", query);
      const response = await axios.get(`${API}/history?${params}`);
      if (requestNo !== historyRequest.current) return;
      const data = response?.data || {};
      setHistory(data.result || []);
      setHistoryTotal(data.total || 0);
      setHistoryTotalAll(data.totalAll || 0);
      setHistoryPage(data.page || page);
    } catch (error) {
      if (requestNo !== historyRequest.current) return;
      setHistory([]);
      setHistoryTotal(0);
      showAlert("failed", error?.response?.data?.message || "Failed to load the venue change history");
    } finally {
      if (requestNo === historyRequest.current) setHistoryLoading(false);
    }
  };

  // A new course starts with no search, on page 1.
  useEffect(() => {
    setHistorySearchInput("");
    setHistoryQuery("");
    loadHistory(courseId, 1, "");
    // eslint-disable-next-line
  }, [courseId]);

  const searchHistory = () => {
    const query = historySearchInput.trim();
    setHistoryQuery(query);
    loadHistory(courseId, 1, query);
  };
  const clearHistorySearch = () => {
    setHistorySearchInput("");
    setHistoryQuery("");
    loadHistory(courseId, 1, "");
  };

  // Reload the page being viewed (same page, same search) — the Refresh button,
  // and on coming back to this tab/window, so the Admit Card tags pick up cards
  // regenerated elsewhere (e.g. Single Admit Card generation in another tab).
  const refreshHistory = () => loadHistory(courseId, historyPage, historyQuery);
  const refreshHistoryRef = useRef(refreshHistory);
  refreshHistoryRef.current = refreshHistory;
  useEffect(() => {
    if (!courseId) return undefined;
    let last = 0;
    const onReturn = () => {
      if (document.visibilityState !== "visible") return;
      // focus and visibilitychange often fire together — reload once.
      if (Date.now() - last < 2000) return;
      last = Date.now();
      refreshHistoryRef.current();
    };
    window.addEventListener("focus", onReturn);
    document.addEventListener("visibilitychange", onReturn);
    return () => {
      window.removeEventListener("focus", onReturn);
      document.removeEventListener("visibilitychange", onReturn);
    };
  }, [courseId]);

  const resetForm = () => {
    setOptions(null);
    setExamDate("");
    setBatchId("");
    setVenueId("");
    setReason("");
    setReferenceNo("");
  };

  const loadOptions = async (target) => {
    resetForm();
    if (!target) return;
    setOptionsLoading(true);
    try {
      const response = await axios.get(`${API}/options/${target.venueallocationid}`);
      const data = response?.data?.result || { batches: [], venues: [] };
      setOptions(data);
      // Start from the candidate's current slot, so a venue-only change is one pick.
      const current = data.batches.find((b) => b.exambatchid == target.batchid);
      if (current) {
        setExamDate(current.examdate);
        setBatchId(current.exambatchid);
      }
    } catch (error) {
      showAlert("failed", error?.response?.data?.message || "Failed to load venues for the change");
    } finally {
      setOptionsLoading(false);
    }
  };

  const search = async (keepSelection) => {
    const appNo = applicationNo.trim();
    if (!courseId || !appNo) {
      showAlert("failed", "Select a course and enter an Application Number");
      return;
    }
    hideAlert();
    setResult(null);
    setNotFound(null);
    resetForm();
    setLoading(true);
    try {
      const response = await axios.get(
        `${API}/search?courseid=${courseId}&applicationno=${encodeURIComponent(appNo)}`
      );
      const data = response?.data?.result;
      setResult(data);
      const next =
        data?.allocations?.find((a) => keepSelection && a.venueallocationid === keepSelection) ||
        data?.allocations?.[0];
      setSelectedId(next?.venueallocationid || null);
      await loadOptions(next);
    } catch (error) {
      const message = error?.response?.data?.message || "Failed to search the candidate";
      if (error?.response?.status === 404) setNotFound(message);
      else showAlert("failed", message);
    } finally {
      setLoading(false);
    }
  };

  const examDates = useMemo(
    () => [...new Set((options?.batches || []).map((b) => b.examdate))],
    [options]
  );
  const batchesForDate = (options?.batches || []).filter((b) => b.examdate === examDate);
  const selectedBatch = options?.batches?.find((b) => b.exambatchid == batchId);
  const selectedVenue = options?.venues?.find((v) => v.venueid == venueId);
  // Free seats of a venue in the chosen batch: normal seats are used first, buffer
  // seats only when no normal seat is left.
  const freeSeats = (v) => (batchId ? v.freeByBatch?.[batchId] ?? 0 : 0);
  const freeNormal = (v) => (batchId ? v.normalByBatch?.[batchId] ?? 0 : 0);
  const freeBuffer = (v) => (batchId ? v.bufferByBatch?.[batchId] ?? 0 : 0);
  const seatsLabel = (v) => {
    if (freeSeats(v) === 0) return "full";
    if (freeNormal(v) > 0) return `${freeNormal(v)} seats free`;
    return `${freeBuffer(v)} buffer seats free`;
  };
  const sameSlot = allocation && venueId == allocation.venueid && batchId == allocation.batchid;

  const canSubmit =
    allocation && batchId && venueId && reason.trim() && !sameSlot && selectedVenue && freeSeats(selectedVenue) > 0;

  const submitChange = async () => {
    setSaving(true);
    try {
      const response = await axios.post(`${API}/change`, {
        venueallocationid: allocation.venueallocationid,
        venueid: venueId,
        batchid: batchId,
        reason: reason.trim(),
        referenceno: referenceNo.trim(),
      });
      setConfirmOpen(false);
      setDoneMessage({
        text: response?.data?.message,
        admitCardGenerated: response?.data?.result?.admitCardGenerated,
      });
      showAlert("success", "Venue changed successfully");
      await search(allocation.venueallocationid);
      loadHistory(courseId, 1);
    } catch (error) {
      setConfirmOpen(false);
      showAlert("failed", error?.response?.data?.message || "Failed to change the venue");
      // Buffer seats may have been taken meanwhile - show the current numbers.
      loadOptions(allocation);
    } finally {
      setSaving(false);
    }
  };

  // The whole course's history, fetched only when Excel is asked for.
  const downloadHistory = async () => {
    if (!historyTotalAll) return;
    setHistoryDownloading(true);
    try {
      const response = await axios.get(`${API}/history?courseid=${courseId}`);
      const rows = response?.data?.result || [];
      const course = courseList?.all?.find((c) => c.courseid == courseId)?.coursename || "Course";
      xlsx(
        [{ sheet: "Venue Changes", columns: HISTORY_COLUMNS, content: rows }],
        { fileName: `Venue Change History - ${course}`, extraLength: 3, writeMode: "writeFile" }
      );
    } catch (error) {
      showAlert("failed", error?.response?.data?.message || "Failed to download the venue change history");
    } finally {
      setHistoryDownloading(false);
    }
  };

  const spinner = <CircularProgress size={16} sx={{ color: BUTTON_FG }} />;

  return (
    <Box sx={{ p: { xs: 0, md: 2 } }}>
      {/* Search */}
      <Paper sx={panelSx}>
        <Typography sx={{ fontSize: 18, fontWeight: 600, mb: 0.5 }}>Search Candidate</Typography>
        <Typography sx={{ fontSize: 14, color: MUTED, mb: 2.5 }}>
          Select the course and enter the Application Number of the candidate who asked for a venue change.
        </Typography>

        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs={12} md={5}>
            <Typography sx={labelSx}>Course</Typography>
            <Select
              fullWidth
              size="small"
              value={courseId}
              displayEmpty
              sx={fieldSx}
              onChange={(e) => {
                setCourseId(e.target.value);
                setResult(null);
                setNotFound(null);
                setDoneMessage(null);
                resetForm();
              }}
            >
              <MenuItem value={0} disabled>
                <Typography component="span" sx={{ color: MUTED }}>
                  Select a course
                </Typography>
              </MenuItem>
              {courseList?.all?.map((course, i) => (
                <MenuItem key={i} value={course.courseid} sx={{ whiteSpace: "nowrap" }}>
                  {course.coursename}
                </MenuItem>
              ))}
            </Select>
          </Grid>

          <Grid item xs={12} md={4}>
            <Typography sx={labelSx}>Application No.</Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Enter Application Number"
              value={applicationNo}
              onChange={(e) => setApplicationNo(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setDoneMessage(null);
                  search();
                }
              }}
              inputProps={{ inputMode: "numeric", autoComplete: "off" }}
              InputProps={{
                sx: fieldSx,
                startAdornment: (
                  <InputAdornment position="start">
                    <MdSearch size={20} color={MUTED} />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <Button
              fullWidth
              variant="contained"
              disableElevation
              onClick={() => {
                setDoneMessage(null);
                search();
              }}
              disabled={loading}
              startIcon={loading ? spinner : <MdSearch />}
              sx={primaryButtonSx}
            >
              {loading ? "Searching..." : "Search"}
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {doneMessage && (
        <Alert
          severity={doneMessage.admitCardGenerated ? "warning" : "success"}
          sx={{ mb: 2.5, borderRadius: "10px" }}
          onClose={() => setDoneMessage(null)}
        >
          {doneMessage.text}
        </Alert>
      )}

      {!loading && !result && !notFound && (
        <Paper sx={panelSx}>
          <EmptyState
            icon={MdPersonSearch}
            title="Find a candidate"
            text="Search by Application Number to see the candidate's current venue and move them to another venue."
          />
        </Paper>
      )}

      {!loading && notFound && (
        <Paper sx={panelSx}>
          <EmptyState icon={MdPersonOff} title="Cannot change venue" text={notFound} />
        </Paper>
      )}

      {/* Current allocation */}
      {!loading && allocation && (
        <Paper sx={panelSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 2 }}>
            <Typography sx={{ flex: 1, minWidth: 200, fontSize: 18, fontWeight: 600 }}>
              {allocation.candidatename || "-"}
            </Typography>
            {allocation.ispwd && <Chip color="info" label="PwD: PwD-friendly venues only" />}
            {(allocation.hasvenuechange ?? allocation.isbufferseat) && (
              <Chip color="warning" label="Venue already changed before" />
            )}
          </Box>

          {result.allocations.length > 1 && (
            <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mb: 2 }}>
              {result.allocations.map((a) => (
                <Chip
                  key={a.venueallocationid}
                  label={allocationLabel(a)}
                  color={a.venueallocationid === selectedId ? "primary" : "default"}
                  onClick={() => {
                    setSelectedId(a.venueallocationid);
                    loadOptions(a);
                  }}
                />
              ))}
            </Box>
          )}

          {result.duplicateGroupOthers > 0 && (
            <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px" }}>
              This candidate is in a duplicate group with {result.duplicateGroupOthers} other candidate(s), who were
              seated at the same venue and batch. Changing the venue separates them.
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <Detail label="Application No." value={allocation.applicationnumber} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Detail label="Roll No." value={allocation.rollno} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Detail label="Exam" value={allocation.examname} />
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <Detail label="Admit Card Generated" value={allocation.isadmitcardgenerated ? "Yes" : "No"} />
            </Grid>
            <Grid item xs={12} md={6}>
              <Detail label="Current Venue" value={allocation.venuename} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Detail label="District" value={allocation.district} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Detail label="Exam Date" value={allocation.examdate} />
            </Grid>
            <Grid item xs={12} sm={6} md={2}>
              <Detail label="Batch" value={allocation.batchno} />
            </Grid>
            <Grid item xs={12}>
              <Detail
                label="District Preferences"
                value={[allocation.preference1, allocation.preference2, allocation.preference3, allocation.preference4]
                  .map((d, i) => (d ? `${i + 1}. ${d}` : null))
                  .filter(Boolean)
                  .join("   ")}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          {/* New slot */}
          <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2 }}>Move to</Typography>
          {optionsLoading ? (
            <CircularProgress size={24} />
          ) : options && options.venues.length === 0 ? (
            <Alert severity="warning" sx={{ borderRadius: "10px" }}>
              No venue of this allocation has seats
              {allocation.ispwd ? " and is PwD-friendly" : ""}. Check the venue data.
            </Alert>
          ) : options ? (
            <Grid container spacing={2}>
              <Grid item xs={12} md={3}>
                <Typography sx={labelSx}>Exam Date</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={examDate}
                  displayEmpty
                  sx={fieldSx}
                  onChange={(e) => {
                    setExamDate(e.target.value);
                    setBatchId("");
                  }}
                >
                  <MenuItem value="" disabled>
                    Select exam date
                  </MenuItem>
                  {examDates.map((d) => (
                    <MenuItem key={d} value={d}>
                      {d}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={labelSx}>Batch</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={batchId}
                  displayEmpty
                  disabled={!examDate}
                  sx={fieldSx}
                  onChange={(e) => setBatchId(e.target.value)}
                >
                  <MenuItem value="" disabled>
                    Select batch
                  </MenuItem>
                  {batchesForDate.map((b) => (
                    <MenuItem key={b.exambatchid} value={b.exambatchid}>
                      {batchLabel(b)}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} md={5}>
                <Typography sx={labelSx}>Venue (free seats in this batch)</Typography>
                <Select
                  fullWidth
                  size="small"
                  value={venueId}
                  displayEmpty
                  disabled={!batchId}
                  sx={fieldSx}
                  onChange={(e) => setVenueId(e.target.value)}
                >
                  <MenuItem value="" disabled>
                    Select venue
                  </MenuItem>
                  {options.venues.map((v) => (
                    <MenuItem
                      key={v.venueid}
                      value={v.venueid}
                      disabled={freeSeats(v) === 0 || (v.venueid == allocation.venueid && batchId == allocation.batchid)}
                    >
                      {v.venuename} - {v.district || "-"} ({seatsLabel(v)})
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              <Grid item xs={12} md={8}>
                <Typography sx={labelSx}>Reason *</Typography>
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  minRows={2}
                  placeholder="Why the student asked for the change"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  inputProps={{ maxLength: 500 }}
                  InputProps={{ sx: fieldSx }}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <Typography sx={labelSx}>Reference No. (email / ticket)</Typography>
                <TextField
                  fullWidth
                  size="small"
                  value={referenceNo}
                  onChange={(e) => setReferenceNo(e.target.value)}
                  inputProps={{ maxLength: 100 }}
                  InputProps={{ sx: fieldSx }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  variant="contained"
                  disableElevation
                  disabled={!canSubmit}
                  onClick={() => setConfirmOpen(true)}
                  startIcon={<MdSwapHoriz />}
                  sx={primaryButtonSx}
                >
                  Change Venue
                </Button>
              </Grid>
            </Grid>
          ) : null}
        </Paper>
      )}

      {/* History */}
      {!!courseId && (
        <Paper sx={panelSx}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 2 }}>
            <Typography sx={{ flex: 1, fontSize: 18, fontWeight: 600 }}>
              Venue Change History ({historyTotalAll})
            </Typography>
            {/* Searches on the server when the button is clicked (or Enter is pressed). */}
            <Box
              component="form"
              onSubmit={(e) => {
                e.preventDefault();
                searchHistory();
              }}
              sx={{ display: "flex", gap: 1, alignItems: "center", width: { xs: "100%", sm: "auto" } }}
            >
              <TextField
                size="small"
                value={historySearchInput}
                onChange={(e) => setHistorySearchInput(e.target.value)}
                placeholder="Application no, venue, reason..."
                disabled={!historyTotalAll}
                sx={{ width: { xs: "100%", sm: 260 } }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <MdSearch />
                    </InputAdornment>
                  ),
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disableElevation
                disabled={!historyTotalAll || historyLoading}
                sx={primaryButtonSx}
              >
                Search
              </Button>
              {historyQuery && (
                <Button onClick={clearHistorySearch} disabled={historyLoading} sx={{ textTransform: "none" }}>
                  Clear
                </Button>
              )}
            </Box>
            <Button
              variant="outlined"
              onClick={refreshHistory}
              disabled={historyLoading}
              startIcon={historyLoading ? <CircularProgress size={16} /> : <MdRefresh />}
              sx={outlineButtonSx}
            >
              Refresh
            </Button>
            <Button
              variant="outlined"
              disabled={!historyTotalAll || historyDownloading}
              onClick={downloadHistory}
              startIcon={historyDownloading ? <CircularProgress size={16} /> : <MdFileDownload />}
              sx={outlineButtonSx}
            >
              {historyDownloading ? "Preparing..." : "Download Excel"}
            </Button>
          </Box>
          {historyLoading && !history.length ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
              <CircularProgress size={28} />
            </Box>
          ) : historyTotalAll === 0 ? (
            <Typography sx={{ color: MUTED, fontSize: 14 }}>No venue changes for this course yet.</Typography>
          ) : historyTotal === 0 ? (
            <Typography sx={{ color: MUTED, fontSize: 14 }}>No venue changes match "{historyQuery}".</Typography>
          ) : (
            <>
            {historyQuery && (
              <Typography sx={{ color: MUTED, fontSize: 13, mb: 1 }}>
                {historyTotal} of {historyTotalAll} changes match "{historyQuery}"
              </Typography>
            )}
            <TableContainer sx={{ maxHeight: 420, opacity: historyLoading ? 0.5 : 1 }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell>Changed On</TableCell>
                    <TableCell>Application No</TableCell>
                    <TableCell>Old Venue / Batch</TableCell>
                    <TableCell>New Venue / Batch</TableCell>
                    <TableCell>Reason</TableCell>
                    <TableCell>Admit Card</TableCell>
                    <TableCell>Changed By</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.map((h) => (
                    <TableRow key={h.venuechangelogid}>
                      <TableCell sx={{ whiteSpace: "nowrap" }}>{h.changeddate}</TableCell>
                      <TableCell>{h.applicationno}</TableCell>
                      <TableCell>
                        {h.oldvenuename || "-"}
                        <Typography sx={{ fontSize: 12, color: MUTED }}>
                          {h.olddistrict} · Batch {h.oldbatchno ?? "-"} · {h.oldexamdate || "-"}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {h.newvenuename}
                        <Typography sx={{ fontSize: 12, color: MUTED }}>
                          {h.newdistrict} · Batch {h.newbatchno ?? "-"} · {h.newexamdate || "-"}
                        </Typography>
                        {h.seattype && (
                          <Chip
                            size="small"
                            variant="outlined"
                            color={h.seattype === "Buffer" ? "warning" : "default"}
                            label={`${h.seattype} seat`}
                            sx={{ mt: 0.5 }}
                          />
                        )}
                      </TableCell>
                      <TableCell>
                        {h.reason}
                        {h.referenceno && (
                          <Typography sx={{ fontSize: 12, color: MUTED }}>Ref: {h.referenceno}</Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const s = admitCardStatus(h);
                          if (!s.color) return s.label;
                          return (
                            <>
                              <Chip size="small" color={s.color} label={s.label} />
                              {h.admitcardgeneratedon && (
                                <Typography sx={{ fontSize: 12, color: MUTED, mt: 0.5, whiteSpace: "nowrap" }}>
                                  {h.admitcardgeneratedon}
                                </Typography>
                              )}
                            </>
                          );
                        })()}
                      </TableCell>
                      <TableCell>{h.changedby}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            {historyPageCount > 1 && (
              <TablePagination
                pageCount={historyPageCount}
                currentPage={historyPage}
                handlePageChange={(_, page) => loadHistory(courseId, page)}
              />
            )}
            </>
          )}
        </Paper>
      )}

      {/* Confirmation */}
      <Dialog open={confirmOpen} onClose={() => !saving && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Change venue for {allocation?.applicationnumber}?</DialogTitle>
        <DialogContent dividers>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell>Current</TableCell>
                <TableCell>New</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <TableCell>Venue</TableCell>
                <TableCell>{allocation?.venuename || "-"}</TableCell>
                <TableCell>{selectedVenue?.venuename}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>District</TableCell>
                <TableCell>{allocation?.district || "-"}</TableCell>
                <TableCell>{selectedVenue?.district || "-"}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Exam Date</TableCell>
                <TableCell>{allocation?.examdate || "-"}</TableCell>
                <TableCell>{selectedBatch?.examdate}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Batch</TableCell>
                <TableCell>{allocation?.batchno ?? "-"}</TableCell>
                <TableCell>{selectedBatch?.batchno}</TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Seat</TableCell>
                <TableCell>{allocation?.isbufferseat ? "Buffer seat" : "Normal seat"}</TableCell>
                <TableCell>
                  {selectedVenue && freeNormal(selectedVenue) > 0
                    ? `Normal seat (${freeNormal(selectedVenue)} free)`
                    : `Buffer seat (${selectedVenue ? freeBuffer(selectedVenue) : 0} free) - no normal seat left`}
                </TableCell>
              </TableRow>
              <TableRow>
                <TableCell>Roll No</TableCell>
                <TableCell>{allocation?.rollno || "-"}</TableCell>
                <TableCell>{allocation?.rollno || "-"} (unchanged)</TableCell>
              </TableRow>
            </TableBody>
          </Table>
          {allocation?.isadmitcardgenerated && (
            <Alert severity="warning" sx={{ mt: 2, borderRadius: "10px" }}>
              The admit card is already generated and shows the current venue. Regenerate it from Single Admit Card
              generation after this change.
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setConfirmOpen(false)} disabled={saving}>
            Cancel
          </Button>
          <Button
            variant="contained"
            disableElevation
            onClick={submitChange}
            disabled={saving}
            startIcon={saving ? spinner : <MdSwapHoriz />}
            sx={primaryButtonSx}
          >
            {saving ? "Changing..." : "Confirm Change"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
